<template>
  <PSelectInput
    v-model="selectedSearchItem"
    class="global-search-input"
    use-input
    fill-input
    hide-selected
    hide-dropdown-icon
    no-options-text=""
    :emit-value="false"
    :map-options="false"
    placeholder="Search"
    :options="searchOptions"
    :options-loading="loading"
    option-label="label"
    :hint="searchHint"
    popup-content-class="global-search-dropdown"
    @filter="onSearch"
    @update:model-value="onSearchSelect"
  >
    <template #no-option>
      <q-item v-if="shouldShowNoResults" dense class="text-neutral text-sm">
        <q-item-section>No results</q-item-section>
      </q-item>
    </template>

    <template #prepend>
      <SearchIcon class="icon--sm text-neutral-50" />
    </template>

    <template #option="scope">
      <!-- GROUP HEADER -->
      <div
        v-if="scope.opt.isGroup"
        class="q-pt-xs q-px-md text-neutral-100 text-sm q-mt-sm"
      >
        {{ scope.opt.label }}
        <span class="search-group-count"> ({{ scope.opt.count }}) </span>
        <q-separator class="q-mt-xs" />
      </div>

      <!-- SEARCH ITEM -->
      <q-item v-else v-bind="scope.itemProps" dense>
        <q-item-section>
          <div class="text-sm text-neutral-80" style="padding: 10px 0px">
            <span v-if="scope.opt.categoryKey === 'user'">
              <span
                v-html="scope.opt.highlightedLabel || scope.opt.label"
              ></span>
              <span v-if="scope.opt.raw?.meta_data?.gstin">
                | GSTIN:
                <span
                  v-html="
                    highlightMatch(
                      scope.opt.raw?.meta_data?.gstin,
                      currentQuery,
                    )
                  "
                ></span>
              </span>

              <span v-else> | GSTIN: N/A </span>
              <span v-if="scope.opt.raw?.meta_data?.role">
                ({{ getRoleLabel(scope.opt.raw?.meta_data?.role) }})
              </span>
            </span>
            <span v-else-if="isSpecialDisplayType(scope.opt.categoryKey)">
              <span
                v-html="scope.opt.highlightedLabel || scope.opt.label"
              ></span>

              <span>
                |
                {{ scope.opt.raw?.name }}
              </span>
            </span>
            <span
              v-else
              v-html="scope.opt.highlightedLabel || scope.opt.label"
            ></span>

            <!-- State for users -->
            <div
              v-if="
                scope.opt.categoryKey === 'user' &&
                scope.opt.raw?.meta_data?.state
              "
              class="text-xs text-neutral-70"
            >
              State: {{ scope.opt.raw?.meta_data?.state }}
            </div>
          </div>
        </q-item-section>
      </q-item>
    </template>
  </PSelectInput>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import SearchIcon from "assets/icons/search.svg";
import { index } from "src/api/global-search";
import { useSearchHint } from "src/composables/searchHint";
import { debounce } from "quasar";

import { generateRoute } from "src/assets/scripts/utilities/routeGenerator";

import {
  getDisplayText,
  filterUserDocs,
  getCategoryLabel,
  buildGroupHeader,
  isSpecialDisplayType,
} from "src/assets/scripts/utilities/helpers";

import { CancelToken } from "src/assets/scripts/utilities/api-clients";

const router = useRouter();

const roleDisplayMap = {
  buyer: "Customer",
  seller: "Vendor",
};

const getRoleLabel = (role) => roleDisplayMap[role] || role;

const searchOptions = ref([]);
const loading = ref(false);
const selectedSearchItem = ref(null);
const hasTyped = ref(false);
const currentQuery = ref("");

let latestRequestId = 0;

const debouncedFetch = debounce((searchTerm, update) => {
  executeFetch(searchTerm, update);
}, 300);

const onSearch = (searchTerm, update) => {
  currentQuery.value = searchTerm;

  hasTyped.value = !!searchTerm;

  if (!searchTerm || searchTerm.length < 3) {
    debouncedFetch.cancel();

    latestRequestId++;

    selectedSearchItem.value = null;

    update(() => {
      searchOptions.value = [];
    });

    return;
  }

  debouncedFetch(searchTerm, update);
};

const executeFetch = async (searchTerm, update) => {
  const requestId = ++latestRequestId;

  try {
    loading.value = true;

    const response = await index({
      params: {
        search: searchTerm,
      },
    });

    if (requestId !== latestRequestId) return;

    if (!response) {
      update(() => {
        searchOptions.value = [];
      });

      return;
    }

    const options = buildOptions(response.data.data, searchTerm);

    update(() => {
      searchOptions.value = options;
    });
  } catch (error) {
    if (requestId !== latestRequestId) return;

    if (CancelToken.isCancel(error)) {
      console.warn("[GlobalSearch] request cancelled:", error.message);
    } else {
      console.error("[GlobalSearch] search request failed:", error);

      update(() => {
        searchOptions.value = [];
      });
    }
  } finally {
    if (requestId === latestRequestId) {
      loading.value = false;
    }
  }
};

const { searchHint } = useSearchHint({
  search: currentQuery,
});

const shouldShowNoResults = computed(
  () => hasTyped.value && currentQuery.value.length >= 3 && !loading.value,
);

const onSearchSelect = (option) => {
  if (!option || option.isGroup) return;

  const route = generateRoute(option.categoryLabel, option.raw, {
    openDrawer: true,
  });

  if (!route) return;

  router.push(route);

  searchOptions.value = [];
};

const highlightMatch = (text, query) => {
  if (!query) return text;

  const normalizedText = text.normalize("NFKC");
  const normalizedQuery = query.normalize("NFKC").trim();

  const escaped = normalizedQuery.replaceAll(
    /[-/.*+?^${}()|[\]\\]/g,
    String.raw`\$&`,
  );

  const regex = new RegExp(escaped, "gi");

  return normalizedText.replace(
    regex,
    (match) =>
      `<mark class="text-primary-100 bg-primary-40 radius-3 text-weight-medium">${match}</mark>`,
  );
};

const buildOptions = (responseData, query) => {
  const byType = responseData?.byType;

  if (!Array.isArray(byType)) return [];

  return byType.flatMap((group) => {
    const { type, documents, returnedCount } = group;

    if (!Array.isArray(documents) || documents.length === 0) return [];

    const filteredDocs =
      type === "user" ? filterUserDocs(documents) : documents;

    if (filteredDocs.length === 0) return [];

    const categoryLabel = getCategoryLabel(type);
    const header = buildGroupHeader(
      type,
      categoryLabel,
      filteredDocs,
      returnedCount,
      documents,
    );

    const items = filteredDocs.map((item) => {
      const text = getDisplayText(item, query);

      return {
        label: text,
        highlightedLabel: highlightMatch(text, query),
        value: String(item._id),
        categoryLabel,
        categoryKey: type,
        raw: item,
      };
    });

    return [header, ...items];
  });
};
</script>

<style scoped>
.global-search-input {
  margin: 20px auto 0 0;
  width: 552px;
}
</style>
